import { HStack, Link as TriggerLink } from "@chakra-ui/react"
import { Link } from "@hydrologiq/shared/components/Link"
import Popover from "@hydrologiq/shared/chakra/Popover"
import EntityPopoverList from "../popovers/EntityPopoverList"

interface Order {
  id: string
  name: string
  status: string
  start: string
  end: string
  quantity?: number | null
  quantity_consumed?: number | null
  customer?: { id: string; name: string } | null
  site?: { id: string; name: string } | null
  sites?: Array<{
    customer: { id: string; name: string }
    site: { id: string; name: string }
  }>
  assets?: Array<{
    id: string
    name: string
    kind: string
    status: string
    category: string
    class: string
    volume?: number | null
  }>
}

interface MultiOrderDisplayProps {
  orders: Order[]
  maxVisible?: number
}

/**
 * Display multiple orders with a "show more" popover for overflow.
 * Shows first N orders inline, with remaining orders in a popover.
 */
const MultiOrderDisplay: React.FC<MultiOrderDisplayProps> = ({ orders, maxVisible = 1 }) => {
  const [isOpen, setIsOpen] = useState(false)

  if (!orders || orders.length === 0) {
    return <span>-</span>
  }

  // Helper function to format order display name
  const formatOrderName = (order: Order) => {
    const siteName = order.sites?.[0]?.site?.name || order.site?.name
    if (siteName) {
      return `${order.name} (${siteName})`
    }
    return order.name
  }

  // Single order - display directly
  if (orders.length === 1) {
    const order = orders[0]
    return (
      <Link href={`/orders?order=${order.id}`} variant="primary">
        {formatOrderName(order)}
      </Link>
    )
  }

  // Multiple orders - show first N + popover for rest
  const visibleOrders = orders.slice(0, maxVisible)
  const remainingOrders = orders.slice(maxVisible)
  const remainingCount = remainingOrders.length

  return (
    <HStack gap={2} flexWrap="wrap">
      {visibleOrders.map((order) => (
        <Link key={order.id} href={`/orders?order=${order.id}`} variant="primary">
          {formatOrderName(order)}
        </Link>
      ))}
      {remainingCount > 0 && (
        <Popover
          trigger={<TriggerLink variant="secondary" fontSize="1em">+{remainingCount} more</TriggerLink>}
          showArrow={true}
          placement="bottom"
          width="17.5rem"
          closeOnOutside
        >
          <EntityPopoverList
            title="Orders"
            count={remainingCount}
            items={remainingOrders.map((order) => ({
              id: order.id,
              name: formatOrderName(order),
              href: `/orders?order=${order.id}`,
            }))}
          />
        </Popover>
      )}
    </HStack>
  )
}
export default MultiOrderDisplay
